import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { X, Copy, Share2, Loader2, Download } from 'lucide-react';
import { sendWork, pickupWork, pickupLink, fmtCode, fmtKey, splitCode } from '../lib/handoff';

// leisure: send today's tour (with photos) from the phone, pick it up on the PC via link / QR / code
export default function TourHandoff({ t, project, importFile, onClose }) {
  const [pct, setPct] = useState(0);
  const [code, setCode] = useState(null);
  const [key, setKey] = useState('');
  const [qr, setQr] = useState(null);
  const [inCode, setInCode] = useState('');
  const [busy, setBusy] = useState(false);
  const link = code ? pickupLink(code, key) : '';
  useEffect(() => { if (link) QRCode.toDataURL(link, { width: 420, margin: 2 }).then(setQr); }, [link]);
  const send = async () => {
    setBusy(true);
    try { const r = await sendWork(project, setPct); setKey(r.key); setCode(r.code); } catch { toast.error(t.hoErr); }
    setBusy(false);
  };
  const pick = async () => {
    const [c, k] = splitCode(inCode);
    if (c.length !== 10 || k.length !== 16) return toast.error(t.hoBadCode);
    setBusy(true);
    try { await pickupWork(c, k, importFile, setPct); toast.success(t.hoGot); onClose(); } catch (e) { toast.error(e.message === 'key' ? t.hoKeyErr : t.hoNotFound); }
    setBusy(false);
  };
  const shareLink = () => (navigator.share ? navigator.share({ title: project.title, text: t.hoShareText, url: link }).catch(() => {}) : navigator.clipboard?.writeText(link).then(() => toast.success(t.shCopied)));
  return createPortal(
    <div className="editor-backdrop" data-testid="handoff-dialog" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="editor ho-dlg">
        <div className="editor-head"><h4>📲➜💻 {t.hoTitle}</h4><button className="icon-btn" data-testid="handoff-close-btn" disabled={busy} onClick={onClose}><X size={18} /></button></div>
        <section className="tr-sec">
          <b>{t.hoSendTitle}</b>
          <p className="muted small-hint">{t.hoSendHint} <b>{project.title}</b></p>
          {!code && <button className="btn btn-green" data-testid="handoff-send-btn" disabled={busy} onClick={send}>{busy ? <Loader2 size={16} className="spin" /> : <Share2 size={16} />} {busy ? `${Math.round(pct * 100)} %` : t.hoSend}</button>}
          {code && (
            <div className="ho-result" data-testid="handoff-result">
              {qr && <img src={qr} alt="QR" className="ho-qr" data-testid="handoff-qr" />}
              <div className="ho-info">
                <small>{t.hoCode}</small>
                <b className="ho-code" data-testid="handoff-code">{fmtCode(code)}-{fmtKey(key)}</b>
                <small className="muted" data-testid="handoff-e2e-note">🔒 {t.hoE2e}</small>
                <code className="tr-url" data-testid="handoff-link">{link}</code>
                <div className="row">
                  <button className="btn btn-dark btn-sm" data-testid="handoff-copy-btn" onClick={() => navigator.clipboard?.writeText(link).then(() => toast.success(t.shCopied), () => window.prompt(t.copy, link))}><Copy size={14} /> {t.copy}</button>
                  <button className="btn btn-share btn-sm" data-testid="handoff-share-btn" onClick={shareLink}><Share2 size={14} /> {t.hoShareLink}</button>
                </div>
                <small className="muted">{t.hoPrivacy}</small>
              </div>
            </div>
          )}
        </section>
        <section className="tr-sec">
          <b>{t.hoPickTitle}</b>
          <div className="row">
            <input className="ho-input" data-testid="handoff-code-input" value={inCode} maxLength={36} placeholder="ABCDE-FGHJK-XXXX-XXXX-XXXX-XXXX" onChange={(e) => setInCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && pick()} />
            <button className="btn btn-proj" data-testid="handoff-pick-btn" disabled={busy} onClick={pick}><Download size={16} /> {t.hoPick}</button>
          </div>
        </section>
      </div>
    </div>,
    document.body,
  );
}
